import React, { useState, useEffect, SyntheticEvent, useRef, FC } from 'react';
import { useLocation, useNavigate } from '@reach/router';
import classnames from 'classnames';
import { Collapse } from 'react-bootstrap';
import { useStaticQuery, graphql, Node as GatsbyGraphQLNode } from 'gatsby';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { useLocale } from 'providers/locale-provider';
import { ScrollTop } from './constant';
import { removeLeadingSlash, removeTrailingSlash } from 'utils/url';
import { getPageName, renderPath, getLangByLocale } from 'shared-utils';
import { LangMenu } from 'shared-constants';

import './header.scss';

const LANGUAGE = 'language';

interface HeaderProps {
  isSticky: boolean;
}

interface NavItem extends GatsbyGraphQLNode {
  name: string;
  slug?: string;
  childrenNavItem?: NavItem[];
  i18n: { en: string; tw: string; cn: string };
  isInApp: boolean;
  timestamp: number;
}

const Header: FC<HeaderProps> = ({ isSticky: isStickyProp }) => {
  const { allNavTree, allNavItem, site } = useStaticQuery(graphql`
    fragment NavItemFragment on NavItem {
      id
      name
      slug
      isInApp
      index
      langs
      i18n {
        en
        tw
        cn
      }
    }
    query {
      allNavTree: allNavItem(
        filter: { parent: { id: { eq: null } } }
        sort: { order: ASC, fields: index }
      ) {
        nodes {
          ...NavItemFragment
          childrenNavItem {
            ...NavItemFragment
            parent {
              id
            }
          }
        }
      }
      allNavItem(sort: { order: ASC, fields: index }) {
        nodes {
          ...NavItemFragment
          childrenNavItem {
            ...NavItemFragment
            parent {
              id
            }
          }
        }
      }
      site {
        siteMetadata {
          supportedLangs: langs
        }
      }
    }
  `);
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useLocale();
  const subMenuRefs = useRef<HTMLElement[]>([]);
  const menuRef = useRef<HTMLElement | null>(null);
  const navToggleRef = useRef<HTMLButtonElement | null>(null);
  const [currentName, setCurrentName] = useState(
    removeTrailingSlash(removeLeadingSlash(getPageName(location.pathname))),
  );
  const { langs = site.siteMetadata.supportedLangs } =
    allNavItem.nodes.find(node => node.name === currentName) || {};
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [subMenuName, setSubMenuName] = useState<string | null>(null);
  const [isSticky, setIsSticky] = useState(isStickyProp);
  useEffect(() => {
    const scroll = () => {
      setIsSticky(isStickyProp || window.scrollY > ScrollTop);
    };
    window.addEventListener('scroll', scroll);
    return () => {
      window.removeEventListener('scroll', scroll);
    };
  }, []);
  useEffect(() => {
    setIsSticky(isStickyProp);
  }, [isStickyProp]);
  useEffect(() => {
    setIsSticky(isStickyProp || window.scrollY > ScrollTop);
    setCurrentName(
      location.hash.match(/#(\w*)/)?.[1] ||
        removeTrailingSlash(removeLeadingSlash(getPageName(location.pathname))),
    );
  }, []);
  useEffect(() => {
    function subMenuBackdrop(e: CustomEvent) {
      if (
        subMenuRefs.current.length !== 0 &&
        !subMenuRefs.current.some(el => el.contains(e.target as Node))
      ) {
        setSubMenuName(null);
      }
    }

    function menuBackdrop(e: CustomEvent) {
      if (
        menuRef.current !== null &&
        navToggleRef.current !== null &&
        !navToggleRef.current.contains(e.target as Node) &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setIsCollapsed(true);
      }
    }
    window.addEventListener('click', subMenuBackdrop as EventListener, true);
    window.addEventListener('click', menuBackdrop as EventListener, true);
    return () => {
      window.removeEventListener(
        'click',
        subMenuBackdrop as EventListener,
        true,
      );
      window.removeEventListener('click', menuBackdrop as EventListener, true);
    };
  }, []);
  const click = (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const pathname = (e.currentTarget as HTMLAnchorElement).pathname;
    const hash = (e.currentTarget as HTMLAnchorElement).hash;
    const sectionId =
      (hash.match(/#(\w*)/)?.[1] as string) ||
      getPageName(pathname).replace(/\//g, '');

    setCurrentName(sectionId);
    setIsCollapsed(true);
    if (location.pathname !== pathname) {
      navigate(`${pathname}${hash}`);
    } else if (location.pathname === pathname && location.hash !== hash) {
      navigate(`${pathname}${hash}`, { replace: true });
    }
  };
  const changeLang = (e: SyntheticEvent<HTMLButtonElement>) => {
    const { lang } = e.currentTarget.dataset;
    const { pathname } = location;
    const pageName = getPageName(pathname);
    navigate(renderPath(pageName, lang));
  };
  const renderNavItem = (node: NavItem) => {
    const text = node.i18n[getLangByLocale(locale)];
    if (!node.childrenNavItem || node.childrenNavItem.length === 0) {
      return (
        text && (
          <li
            key={node.id}
            className={classnames(currentName === node.name && 'active')}
          >
            {node.isInApp ? (
              <a
                className="page-scroll"
                href={renderPath(node.slug!, { locale })}
                onClick={click}
              >
                {text}
              </a>
            ) : (
              <a
                className="page-scroll"
                href={node.slug}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {text}
              </a>
            )}
          </li>
        )
      );
    }
    return (
      <li
        key={node.id}
        className={classnames(
          node.childrenNavItem.some(item => item.name === currentName) &&
            'active',
        )}
      >
        <div
          ref={el => {
            return (
              el !== null &&
              !subMenuRefs.current.some(subRef => subRef.contains(el)) &&
              subMenuRefs.current.push(el)
            );
          }}
          className="page-scroll has-submenu"
          onClick={() =>
            setSubMenuName(subMenuName !== node.name ? node.name : null)
          }
        >
          <div className="submenu-title">
            {text}
            <FontAwesomeIcon
              icon={faChevronDown}
              className={classnames(
                'icon',
                subMenuName === node.name && 'rotate',
              )}
            />
          </div>
          <Collapse in={subMenuName === node.name}>
            <nav className="submenu">
              {node.childrenNavItem.map(renderNavItem)}
            </nav>
          </Collapse>
        </div>
      </li>
    );
  };
  return (
    <header id="header-wrap">
      <div
        className={classnames(
          'navbar navbar-default navbar-fixed-top menu-top',
          isSticky && 'menu-sticky',
        )}
      >
        <div className="container-fluid ampos-maximum-content-wrapper">
          <div className="navbar-header">
            <button
              ref={navToggleRef}
              type="button"
              className="navbar-toggle"
              data-toggle="collapse"
              data-target=".navbar-collapse"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
            </button>
            <a
              href={renderPath('/', { locale })}
              className="navbar-brand"
              onClick={click}
            >
              <img
                className="img-responsive"
                src="/images/logo.svg"
                alt="logo"
              />
            </a>
          </div>
          <Collapse in={!isCollapsed}>
            <div className="navbar-collapse">
              <nav ref={menuRef} className="nav navbar-nav">
                {allNavTree.nodes.map(renderNavItem)}
                <li>
                  <div
                    ref={el => {
                      return (
                        el !== null &&
                        !subMenuRefs.current.some(subRef =>
                          subRef.contains(el),
                        ) &&
                        subMenuRefs.current.push(el)
                      );
                    }}
                    className="page-scroll has-submenu"
                    onClick={() =>
                      setSubMenuName(subMenuName !== LANGUAGE ? LANGUAGE : null)
                    }
                  >
                    <div className="submenu-title">
                      <div className="globe">
                        &nbsp;
                        <FontAwesomeIcon icon={faGlobe} className="icon" />
                      </div>
                      <div className="language">
                        <FormattedMessage id="header.language" />
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={classnames(
                            'icon',
                            subMenuName === LANGUAGE && 'rotate',
                          )}
                        />
                      </div>
                    </div>
                    <Collapse in={subMenuName === LANGUAGE}>
                      <nav className="submenu submenu-language">
                        {LangMenu.filter(
                          ({ lang }) => langs.indexOf(lang) !== -1,
                        ).map(({ lang, text }) => (
                          <li key={lang}>
                            <button data-lang={lang} onClick={changeLang}>
                              {text}
                            </button>
                          </li>
                        ))}
                      </nav>
                    </Collapse>
                  </div>
                </li>
              </nav>
            </div>
          </Collapse>
        </div>
      </div>
    </header>
  );
};

export default Header;
